import {
    useEffect,
    useLayoutEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    Platform,
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    TextAreaInput,
    PageContainer,
    TextInput,
    SelectBox,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    CreateCompanyRequestRequestType
} from "../../../../../services/restAPI/actions/createCompanyRequest/types";
import type {
    PositionsObjectType
} from "../../../../../services/restAPI/actions/getPositions/types";
import type {
    SectorsObjectType
} from "../../../../../services/restAPI/actions/getSectors/types";
import type AppStackParamList from "../../../../../types/navigation/app";
import type {
    SelectedFileType
} from "../../../editProfile/type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    CircleQuestionMark,
    AlertCircle,
    ChevronLeft,
    Check,
    Pencil
} from "lucide-react-native";
import ImageCropPicker from "react-native-image-crop-picker";
import validator from "validator";
import gmkonyalogo from "../../../../../assets/images/gmkonyalogo.png";
import {
    RESTService
} from "../../../../../services/restAPI";
import {
    handleUpload
} from "../../../../../utils";
import {
    IS_EXPORTS
} from "../constant";

const CreateCompany = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const {
        spaces,
        borders,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const inset = useSafeAreaInsets();

    const [data,
        setData] = useState<CreateCompanyRequestRequestType>({
            aboutUs:"",
            address: "",
            foundationYear: 2026,
            isExports: false,
            mail: "",
            phoneNumber: "",
            positionID: "",
            sectorIDs: [],
            taxNumber: "",
            taxOffice: "",
            title: "",
            website: ""
        });
    const [positionData,
        setPositionData]= useState<Array<PositionsObjectType>>([]);
    const [sectorsData,
        setSectorsData]= useState<Array<SectorsObjectType>>([]);
    const [loading,
        setLoading] = useState(true);
    const [showSuccessDialog,
        setShowSuccessDialog] = useState(false);
    const [showConfirmDialog,
        setShowConfirmDialog] = useState(false);
    const [selectedImage,
        setSelectedImage] = useState<string | null>(null);
    const [selctedFile,
        setSelectedFile] = useState<SelectedFileType | null>(null);

    useEffect(() => {
        getSectors();
        getPositions();
    }, []);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        paddingTop: inset.top,
                        paddingHorizontal: spaces.spacingMd,
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("addCompany")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const getPositions = () => {
        RESTService.action("GetPositions", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setPositionData(res.payload);
                }

                setLoading(false);
            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const getSectors = () => {
        RESTService.action("GetSectors", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setSectorsData(res.payload);
                }

                setLoading(false);
            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const createCompanyRequest = () => {
        if(!data) {
            NCoreUIKitSnackBar.open({
                title: localize("emptyRequestError"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.aboutUs && data.aboutUs.length > 3)) {
            NCoreUIKitToast.open({
                title: localize("aboutCannotBeEmpty"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.mail && validator.isEmail(data.mail))) {
            NCoreUIKitToast.open({
                title: localize("invalidEmail"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.title && data.title.length > 2)) {
            NCoreUIKitToast.open({
                title: localize("companyTitleTooShort"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.address && data.address.length > 2)) {
            NCoreUIKitToast.open({
                title: localize("invalidAddress"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.taxOffice && data.taxOffice.length > 2)) {
            NCoreUIKitToast.open({
                title: localize("invalidTaxOffice"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.taxNumber && data.taxNumber.length > 9)) {
            NCoreUIKitToast.open({
                title: localize("invalidTaxNumber"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.website && validator.isURL(data.website, {
            validate_length: true
        }))) {
            NCoreUIKitToast.open({
                title: localize("invalidWebsite"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.foundationYear && data.foundationYear > 1923 && data.foundationYear < 2026)) {
            NCoreUIKitToast.open({
                title: localize("invalidFoundationYear"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.positionID && data.positionID.length)) {
            NCoreUIKitToast.open({
                title: localize("invalidPosition"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.sectorIDs && data.sectorIDs.length)) {
            NCoreUIKitToast.open({
                title: localize("invalidSector"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        if(!(data.phoneNumber && validator.isMobilePhone(data.phoneNumber))) {
            NCoreUIKitToast.open({
                title: localize("invalidPhoneNumber"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        RESTService.action("CreateCompanyRequest", data)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    if(selctedFile) {
                        handleUpdateProfilePhoto(res.payload.requestID);
                        return;
                    }
                    setShowSuccessDialog(true);
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const handleUpdateProfilePhoto = (reqID: string) => {
        if(!selctedFile) {
            return;
        }

        RESTService.action("CreateCompanyPhoto", {
            requestID: reqID
        })
            .then(async (res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {

                    await handleUpload(selctedFile, res.payload.uploadToken)
                        .then(() => {
                            setShowSuccessDialog(true);
                        })
                        .catch((err) => {
                            console.log("err", err);
                            throw err;
                        });
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const renderProfilePhoto = () => {
        return <View
            style={[
                stylesheet.profilePhotoWrapper,
                {
                    borderRadius: radiuses.full,
                    marginTop: spaces.spacingLg,
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <Image
                source={selectedImage ? {
                    uri: selectedImage
                } : gmkonyalogo}
                resizeMode="contain"
                style={[
                    stylesheet.profilePhotoImage,
                    {
                        borderRadius: radiuses.md
                    }
                ]}
            />
            <TouchableOpacity
                style={[
                    stylesheet.photoEditButton,
                    {
                        right: -spaces.spacingSm,
                        bottom: -spaces.spacingSm,
                        backgroundColor: colors.content.container.mid,
                        borderRadius: radiuses.full,
                        padding: spaces.spacingSm,
                        borderWidth: borders.subtract,
                        borderColor: colors.content.border.subtle
                    }
                ]}
                onPress={() => {
                    ImageCropPicker.openPicker({
                        cropping: true,
                        multiple: false
                    }).then(image => {
                        const format = image.mime?.split("/")[1];
                        if (!format) return;

                        if (image.size > 20 * 1024 * 1024) {
                            NCoreUIKitToast.open({
                                title: localize("imageTooLarge"),
                                type: "warning"
                            });
                            return;
                        }

                        if (![
                            "jpg",
                            "jpeg",
                            "png",
                            "heic"
                        ].includes(format)) {
                            NCoreUIKitToast.open({
                                title: localize("invalidFormat"),
                                type: "warning"
                            });
                            return;
                        }

                        const selectedFile = {
                            name: image.path.split("/").pop(),
                            type: image.mime,
                            uri: Platform.OS === "ios" ? image.path.startsWith("file://") ? image.path : `file:/${image.path}` : image.path
                        } as unknown as SelectedFileType;
                        setSelectedImage(image.path);
                        setSelectedFile(selectedFile);
                    }).catch((err) => {
                        console.error(err);
                    });
                }}
            >
                <Pencil
                    size={20}
                />
            </TouchableOpacity>
        </View>;
    };

    const renderSuccessDialog = () => {
        return <Dialog
            secondaryButtonProps={{
                title: localize("continueEditing" as keyof NCoreUIKit.Translation),
                customBorderColor: colors.project.black,
                customTextColor: colors.project.black,
                customColor: colors.project.brandWhite,
                onPress: () => {
                    setShowSuccessDialog(false);
                    navigation.navigate("MainTabs", {
                        screen: "HomeTab",
                        params: {
                            screen: "Home"
                        }
                    });
                }
            }}
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.system.low.success,
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <Check
                        color={colors.project.brandPrimary}
                        size={40}
                    />
                </View>
                <Text
                    style={stylesheet.dialogTitle}
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("submittedSuccessfully")}
                </Text>
            </View>}
            primaryButtonProps={{
                customBorderColor: colors.project.brandPrimary,
                customTextColor: colors.project.brandWhite,
                customColor: colors.project.brandPrimary,
                title: localize("backToHome"),
                onPress: () => {
                    setShowSuccessDialog(false);
                    navigation.navigate("MainTabs", {
                        screen: "HomeTab",
                        params: {
                            screen: "Home"
                        }
                    });
                }
            }}
            content={localize("requestSentForReview")}
            onOverlayPress={() => {
                setShowSuccessDialog(false);
                navigation.navigate("MainTabs", {
                    screen: "HomeTab",
                    params: {
                        screen: "Home"
                    }
                });
            }}
            isVisible={showSuccessDialog}
            variant="ok"
        />;
    };


    const renderVerificationDialog = () => {
        return <Dialog
            secondaryButtonProps={{
                customBorderColor: colors.project.black,
                customTextColor: colors.project.black,
                customColor: colors.project.brandWhite,
                title: localize("abandon"),
                onPress: () => {
                    setShowConfirmDialog(false);
                }
            }}
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <CircleQuestionMark
                        color={colors.project.gold}
                        size={50}
                    />
                </View>
                <Text
                    style={stylesheet.dialogTitle}
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("confirmCompanyCreationRequest")}
                </Text>
            </View>}
            primaryButtonProps={{
                title: localize("submitForApproval"),
                customBorderColor: colors.project.gold,
                customTextColor: colors.project.brandWhite,
                customColor: colors.project.gold,
                onPress: () => {
                    setShowConfirmDialog(false);
                    createCompanyRequest();
                }
            }}
            content={localize("companyCreationRequestInfo")}
            onOverlayPress={() => {
                setShowConfirmDialog(false);
            }}
            isVisible={showConfirmDialog}
            variant="yes-no"
        />;
    };

    if(loading) {
        return <PageContainer>
            <ActivityIndicator
                size={"large"}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        backgroundColor="default"
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderProfilePhoto()}
        <SelectBox
            initialSelectedItems={[]}
            keyExtractor={(item) => item._id }
            titleExtractor={(item) => item.value}
            spreadBehaviour="stretch"
            data={positionData}
            title={localize("position")}
            isWorkWithRealtime={false}
            isCleanEnabled={false}
            minChoice={1}
            maxChoice={1}
            isMultipleSelect={false}
            selectSheetProps={{
                isWorkAsFullScreen: true
            }}
            onOk={(
                selectedItems
            ) => {
                if(selectedItems && selectedItems.length) {
                    setData({
                        ...data,
                        positionID:  selectedItems[0]?.__key || ""
                    });
                }
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <TextInput
            title={localize("companyOrOrganization")}
            type="default"
            spreadBehaviour="stretch"
            onChangeText={(e) => {
                setData({
                    ...data,
                    title: e
                });
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <TextInput
            title={localize("foundationYear")}
            type="default"
            spreadBehaviour="stretch"
            onChangeText={(e) => {
                setData({
                    ...data,
                    foundationYear: Number(e)
                });
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <SelectBox
            initialSelectedItems={[
            ]}
            keyExtractor={(item) => item._id }
            titleExtractor={(item) => item.value}
            spreadBehaviour="stretch"
            data={sectorsData}
            title={localize("sector")}
            isWorkWithRealtime={false}
            isCleanEnabled={true}
            minChoice={1}
            isMultipleSelect={true}
            selectSheetProps={{
                style:{
                    borderTopRightRadius: radiuses.sm,
                    borderTopLeftRadius: radiuses.sm
                }
            }}
            onOk={(
                selectedItems
            ) => {
                if(selectedItems && selectedItems.length) {
                    const sectorIDs = selectedItems.map(e => e.__key);

                    setData({
                        ...data,
                        sectorIDs: sectorIDs
                    });
                }
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <TextInput
            title={localize("companyEmailAddress")}
            type="default"
            spreadBehaviour="stretch"
            keyboardType="email-address"
            onChangeText={(e) => {
                setData({
                    ...data,
                    mail: e
                });
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <TextAreaInput
            title={localize("aboutCompany")}
            type="default"
            spreadBehaviour="stretch"
            onChangeText={(e) => {
                setData({
                    ...data,
                    aboutUs: e
                });
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <TextInput
            title={localize("address")}
            type="default"
            spreadBehaviour="stretch"
            onChangeText={(e) => {
                setData({
                    ...data,
                    address: e
                });
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <TextInput
            title={localize("companyPhone")}
            type="default"
            spreadBehaviour="stretch"
            keyboardType="phone-pad"
            onChangeText={(e) => {
                setData({
                    ...data,
                    phoneNumber: e
                });
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <TextInput
            title={localize("taxOffice")}
            type="default"
            spreadBehaviour="stretch"
            onChangeText={(e) => {
                setData({
                    ...data,
                    taxOffice: e
                });
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <TextInput
            title={localize("taxNumber")}
            type="default"
            spreadBehaviour="stretch"
            onChangeText={(e) => {
                setData({
                    ...data,
                    taxNumber: e
                });
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <SelectBox
            keyExtractor={(item) => item.key }
            titleExtractor={(item) => item.value}
            spreadBehaviour="stretch"
            data={IS_EXPORTS}
            title={localize("doesExport")}
            isWorkWithRealtime={false}
            minChoice={1}
            maxChoice={1}
            isMultipleSelect={false}
            selectSheetProps={{
                style:{
                    borderTopRightRadius: radiuses.sm,
                    borderTopLeftRadius: radiuses.sm
                }
            }}
            onOk={(
                selectedItems
            ) => {
                if(selectedItems && selectedItems.length) {
                    const _isExports = selectedItems[0]?.__key === "true" ? true : false;

                    setData({
                        ...data,
                        isExports: _isExports
                    });
                }
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <TextInput
            type="default"
            title={localize("website")}
            spreadBehaviour="stretch"
            onChangeText={(e) => {
                setData({
                    ...data,
                    website: e
                });
            }}
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <Button
            size="large"
            title={localize("submitCompanyCreationRequest")}
            customColor="black"
            customBorderColor="black"
            onPress={() => {
                setShowConfirmDialog(true);
            }}
            spreadBehaviour="stretch"
            style={{
                marginBottom: spaces.spacingMd
            }}
        />
        <Button
            size="large"
            title={localize("abandon")}
            onPress={() => {
                navigation.goBack();
            }}
            customBorderColor="black"
            variant="outline"
            spreadBehaviour="stretch"
            style={{
                marginBottom: spaces.spacingXl
            }}
        />
        {renderVerificationDialog()}
        {renderSuccessDialog()}
    </PageContainer>;
};
export default CreateCompany;
